import * as React from "react";
import { Icon } from "../actions/Icon";
import { Badge, type BadgeProps } from "../display/Badge";
import { SegmentedControl } from "../forms/SegmentedControl";
import { Switch } from "../forms/Switch";

const eventPlatformTone: Record<string, BadgeProps["tone"]> = {
  "Google Meet": "meet",
  Zoom: "zoom",
  "Microsoft Teams": "teams",
  Teams: "teams",
};

/**
 * Calendar event with platform, attendees, the auto-record rule that matched, and a per-meeting capture choice (Bot / Desktop / Off).
 */
export interface EventCardProps {
  start: string;
  end?: string;
  title: React.ReactNode;
  platform?: "Google Meet" | "Zoom" | "Microsoft Teams" | (string & {});
  attendees?: number;
  external?: boolean;
  rule?: string;
  /** e.g. "In 12 min" — highlights the card */
  now?: string;
  mode?: "Bot" | "Desktop" | "Off";
  onModeChange?: (mode: string) => void;
  /** Capture choices offered. Default Bot / Desktop / Off; an empty list hides the control */
  modes?: ("Bot" | "Desktop" | "Off")[];
  /** Accessible name for the capture choice, e.g. "Auto-record" */
  modeLabel?: string;
  /** Extra controls under the meta line, e.g. "Send notetaker now" */
  actions?: React.ReactNode;
  /** Popup list row with a bot Switch instead of the segmented control */
  compact?: boolean;
  className?: string;
}

export function EventCard({
  start,
  end,
  title,
  platform,
  attendees,
  external,
  rule,
  now,
  mode = "Off",
  onModeChange,
  modes = ["Bot", "Desktop", "Off"],
  modeLabel = "Capture",
  actions,
  compact,
  className,
}: EventCardProps): JSX.Element {
  if (compact) {
    return (
      <div className={["gk-event", "gk-event--compact", className].filter(Boolean).join(" ")}>
        <span className="gk-event__time">{start}</span>
        <div className="gk-event__body">
          <span className="gk-event__title">{title}</span>
          <span className="gk-caption">{platform}</span>
        </div>
        <Switch
          size="sm"
          checked={mode === "Bot"}
          onChange={(v) => onModeChange?.(v ? "Bot" : "Off")}
          label={typeof title === "string" ? "Send bot to " + title : "Send bot"}
        />
      </div>
    );
  }
  return (
    <div className={["gk-event", now && "gk-event--now", className].filter(Boolean).join(" ")}>
      <div className="gk-event__time">
        {start}
        {end && <div className="gk-event__end">{end}</div>}
      </div>
      <div className="gk-event__body">
        <div className="gk-event__title">
          <span>{title}</span>
          {now && (
            <Badge tone="now-solid" size="sm">
              {now}
            </Badge>
          )}
        </div>
        <div className="gk-event__meta">
          {platform && (
            <Badge tone={eventPlatformTone[platform] || "neutral"} size="sm">
              {platform}
            </Badge>
          )}
          {attendees !== undefined && (
            <span className="gk-event__count">
              <Icon name="users" size={15} />
              {attendees}
            </span>
          )}
          {external && (
            <Badge tone="record" size="sm">
              External
            </Badge>
          )}
        </div>
        {rule && (
          <span className="gk-event__rule">
            <Icon name="lightning" size={15} />
            {rule}
          </span>
        )}
        {actions && <div className="gk-event__actions">{actions}</div>}
      </div>
      {modes.length > 0 && <SegmentedControl options={modes} value={mode} onChange={onModeChange} label={modeLabel} />}
    </div>
  );
}
